/**
 * useDoSync — the unified sync engine.
 *
 * One hook that drives any DO scope (UserDo or StudyGroupDo) by accepting
 * a `ScopeAdapter` that captures the scope-specific bits (which store to
 * write to, how to build sync-init / apply sync-diff, what protocol
 * messages exist). Everything else — WebSocket lifecycle, decode, log,
 * op-ack/op-rejected, persistence-error handling, dirty-detection
 * scaffolding — is shared.
 *
 * This replaces the parallel implementations in:
 *   - `use-user-do-sync.ts` (UserDo)
 *   - `use-study-group-do-sync.ts` (StudyGroupDo)
 *   - `sync-message-handlers.ts` (UserDo routing)
 *   - `sg-dispatch.ts` (StudyGroupDo routing)
 *
 * The adapters reuse the existing scope-specific logic — this engine is
 * about deduplication, not rewriting domain logic.
 */

import { notifySocketCredentialRejected } from '@flashka/shared/auth/jwt-cache';
import { decodeCustomPayload } from '@flashka/shared/sync-engine/framework/sync-compression';

import * as Sentry from '@sentry/react';
import { useEffect, useRef } from 'react';
import type YProvider from 'y-partyserver/provider';

import { isSyncMessageLogEnabled, pushSyncMessage } from '../message-log';
import { perfLog, perfMark, perfNow } from '../perf-log'; // [perf-probe]
import {
  isPersistenceErrorMessage,
  showPersistenceErrorToast,
} from '../persistence-error-toast';
import {
  forgetFlushMutation,
  noteFlushMutationRejected,
} from '../sync-bookkeeping/flush-reject-guard';
import { getPendingMutationsTracker } from '../sync-bookkeeping/pending-mutations-singleton';
import { getPreImageStash } from '../sync-bookkeeping/pre-image-stash';
import { getRejectionToastEmitter } from '../sync-bookkeeping/sync-rejection-toast';

// ---------------------------------------------------------------------------
// Scope adapter contract
// ---------------------------------------------------------------------------

/**
 * Write raw JSON to this scope's socket, and say whether it went out.
 *
 * The boolean is load-bearing, not decoration: the socket may be closed (a
 * dropped connection mid-flush, a backgrounded app), in which case the message
 * is DISCARDED with no error and no throw. A caller that treats "enqueued" as
 * "delivered" then retires state it still needs — which is how an offline card
 * edit could be forgotten: its deck's unsynced mark was cleared after a push
 * the closed socket had silently dropped, so the next launch did not load that
 * deck's rows and nothing ever pushed them.
 */
export type SendRaw = (raw: string) => boolean;

/**
 * A `ScopeAdapter` is the entire contract the engine needs to drive a
 * sync scope. Implement once per scope (UserDo / StudyGroupDo) — the
 * engine handles everything else.
 *
 * The engine routes incoming messages by trying handlers in this order:
 *   1. Universal handlers (op-ack, op-rejected, persistence-error)
 *   2. `adapter.onProtocolMessage(msg)` for scope-specific control
 *      messages (sync-state, sync-diff, entity broadcasts, etc.)
 *   3. Everything else is logged + dropped.
 *
 * Outbound: the adapter exposes `subscribeDirty(send)` which calls
 * `send(json)` whenever a local change should be pushed. The engine
 * wires it to the WebSocket.
 */
export type ScopeAdapter = {
  /**
   * Handle a decoded inbound message that wasn't an op-ack /
   * op-rejected / persistence-error. Return `true` if the adapter
   * consumed it; `false` to let the engine log it as unhandled.
   *
   * The send callback writes raw JSON to the socket and reports whether it
   * actually went out — see {@link SendRaw}.
   */
  onProtocolMessage: (msg: unknown, send: SendRaw) => boolean | Promise<boolean>;

  /**
   * Subscribe to local store changes. Calls the supplied `send`
   * function for every dirty change that should go outbound. Returns a
   * cleanup function (called when the engine unmounts).
   *
   * Coalescing / debouncing is the adapter's responsibility — the
   * engine just forwards `send(raw)` to the socket.
   */
  subscribeDirty: (send: SendRaw) => () => void;

  /**
   * Called after the WebSocket connects and the provider has reported
   * `sync` for the first time. Adapter sends any setup messages (e.g.
   * `sync-init` for handshake-driven scopes). Optional — scopes that
   * wait for a server-initiated `sync-state` can leave this undefined.
   */
  onConnected?: (send: SendRaw) => void;

  /**
   * Called when the WebSocket disconnects (any reason). Adapter
   * resets its scope-local state if needed.
   */
  onDisconnected?: () => void;

  /**
   * Called exactly once when the engine unbinds (unmount, user switch,
   * provider swap). Release adapter-held resources — pending timers,
   * subscriptions — WITHOUT touching status state: the socket may be
   * alive and a new binding may take over. Distinct from
   * `onDisconnected`, which is a connection event, not a lifecycle one
   * (and does not fire on unbind — `connection-close` arrives after the
   * listener is already off).
   */
  onDispose?: () => void;

  /**
   * Called when one of our own flush pushes is acked by the server
   * (universal `op-ack`), with the settled `mutationId`. A scope can use
   * it to advance "server has confirmed" bookkeeping — the SG scope
   * advances its per-deck shared-card clock so the flush doesn't re-push
   * an owned card on the next sync-diff. Invoked BEFORE the engine drops
   * the flush bookkeeping, so a peek of that bookkeeping still resolves.
   * Optional.
   */
  onPushAck?: (mutationId: string) => void;

  /**
   * Called when the server refused one of our pushes (universal
   * `op-rejected`). A `validation` refusal is as final as an ack, so a scope
   * that holds bookkeeping until the answer settles it here; any other reason
   * (the handler threw) may pass, and the scope may re-send. Returns `true`
   * for a background push the scope owns (a card flush): no user action is
   * waiting on it, so no "couldn't save" toast. Optional.
   */
  onPushRejected?: (
    mutationId: string,
    send: SendRaw,
    reason: OpRejectedMessage['reason'],
  ) => boolean;

  /**
   * Called when the server has terminally rejected this connection's
   * credentials — `AUTH_CLOSE_TERMINAL_THRESHOLD` consecutive 4401/4403
   * closes without an intervening successful sync. The engine has
   * already called `provider.disconnect()` (retrying would just be
   * rejected again); the adapter surfaces the state (e.g.
   * `setSyncStatus('error')`) and reports it. Optional.
   */
  onAuthRejected?: (code: number, reason?: string) => void;
};

// ---------------------------------------------------------------------------
// Engine config
// ---------------------------------------------------------------------------

export type DoSyncEngineConfig = {
  /** The y-partyserver provider, already constructed by the caller. */
  provider: YProvider | null;
  /**
   * Scope label for the devtools message log + debug output. One of
   * `'user-do'` or `'study-group-do'`.
   */
  scopeLabel: 'user-do' | 'study-group-do';
  /**
   * Stable identifier for the room (userId or groupId). Used to clear
   * adapter state on room changes and to namespace devtools traces.
   */
  roomId: string | null | undefined;
  /** The scope-specific adapter. */
  adapter: ScopeAdapter;
};

// ---------------------------------------------------------------------------
// Engine lifecycle (pure — non-React)
// ---------------------------------------------------------------------------

/**
 * Engine-shaped subset of `YProvider`. The engine only needs to send
 * messages, inspect connection state, and listen to a small set of
 * events. Pulling this out lets the engine be tested without dragging
 * in `y-partyserver`.
 */
export type EngineProvider = {
  ws?: { readyState: number };
  sendMessage(raw: string): void;
  /**
   * Stop the underlying socket AND its auto-reconnect loop. Optional
   * because test fakes may omit it; the real y-partyserver provider
   * has it. The engine calls it when the server has terminally
   * rejected the connection's credentials (see `WsCloseEvent`).
   */
  disconnect?(): void;
  /**
   * Re-open after `disconnect()`. Optional for the same reason; the real
   * provider re-resolves its `params` (a fresh token) before connecting.
   */
  connect?(): void | Promise<void>;
  /**
   * The URL of the current/last socket, query params included. y-partyserver
   * writes the resolved `params` here before opening, and emits
   * `connection-close` before refreshing them — so at close time this is the
   * URL the rejected socket actually used.
   */
  url?: string;
  on(event: 'custom-message', handler: (payload: string) => void): void;
  on(event: 'sync', handler: (synced: boolean) => void): void;
  on(event: 'connection-close', handler: (event?: WsCloseEvent) => void): void;
  off(event: 'custom-message', handler: (payload: string) => void): void;
  off(event: 'sync', handler: (synced: boolean) => void): void;
  off(event: 'connection-close', handler: (event?: WsCloseEvent) => void): void;
};

/**
 * Subset of the DOM CloseEvent that y-partyserver forwards on
 * `connection-close`. `code` 4401/4403 are the server's auth-failure
 * closes (worker upgrade rejection and DO-level rejection both use
 * them — see `party/worker.ts` and `party/framework/sync-yserver.ts`).
 */
export type WsCloseEvent = { code?: number; reason?: string };

/**
 * Consecutive 4401/4403 closes (with no successful sync in between)
 * before the engine treats the rejection as terminal. > 1 because a
 * single 4401 can be a transiently expired token — the provider
 * re-fetches a fresh token via its async `params` on every reconnect
 * attempt, so a refreshable session heals on the next try. Three
 * misses means the credential itself is dead: stop hammering the
 * server every ~10s forever and surface the failure instead.
 */
export const AUTH_CLOSE_TERMINAL_THRESHOLD = 3;

/**
 * Pauses between reconnect bursts when the auth closes came from sockets
 * that carried NO token (the client could not mint one: an iOS keychain item
 * unreadable while the phone is locked and the app runs in the background,
 * or the token endpoint unreachable offline). Those closes say nothing about
 * the credential, so they must not trip the terminal halt — which would stop
 * sync until a fresh sign-in, long after the phone unlocks (Sentry
 * FLASHKA-APP-ZH: ~40% of the "auth rejected" alarms were backgrounded apps).
 * But retrying every ~10s forever is what the threshold exists to prevent,
 * so each burst of AUTH_CLOSE_TERMINAL_THRESHOLD token-less closes pauses
 * the socket for the next delay here (the last one repeats), then reconnects
 * with freshly resolved params. Capped at a minute: nothing resumes a pause
 * early, so the cap is how long sync can stay off after the phone unlocks.
 */
export const NO_TOKEN_RETRY_DELAYS_MS = [30_000, 60_000] as const;

/**
 * Token-less pauses in a row (no successful sync between) after which the
 * engine REPORTS, once per scope per JS session. A locked phone heals within a
 * pause or two; this many (~2.5 min of closes with no token) means the client
 * cannot mint one at all — an auth outage or a keychain that never unlocks —
 * which used to be invisible: only a console line, never terminal.
 */
export const NO_TOKEN_REPORT_AFTER_PAUSES = 3;
const reportedNoTokenScopes = new Set<string>();

/** Test-only: forget which scopes already reported persistent token-less closes. */
export function _resetNoTokenReports(): void {
  reportedNoTokenScopes.clear();
}

/**
 * `false` when the socket that just closed provably sent no `token` param;
 * `null` when the provider exposes no URL (test fakes) — treated as a
 * token-bearing close, i.e. today's behaviour.
 */
export function closedSocketCarriedToken(
  provider: Pick<EngineProvider, 'url'>,
): boolean | null {
  if (!provider.url) return null;
  try {
    return new URL(provider.url).searchParams.has('token');
  } catch {
    return null;
  }
}

/** The `token` param the socket connected with, when the provider exposes it. */
function closedSocketToken(provider: Pick<EngineProvider, 'url'>): string | null {
  if (!provider.url) return null;
  try {
    return new URL(provider.url).searchParams.get('token');
  } catch {
    return null;
  }
}

/**
 * Whether a close is the server refusing this socket's CREDENTIAL: a 4401 /
 * 4403 from a socket that carried a token. A token-less auth close (the
 * client could not mint one — see NO_TOKEN_RETRY_DELAYS_MS) is not: the
 * engine retries it on a backoff, and a caller must not tear the room down
 * for it.
 */
export function isCredentialRejection(
  event: WsCloseEvent | undefined,
  provider: Pick<EngineProvider, 'url'>,
): boolean {
  const code = event?.code;
  if (code !== 4401 && code !== 4403) return false;
  return closedSocketCarriedToken(provider) !== false;
}

/**
 * Start the engine. Returns a cleanup function. Pure plumbing — call
 * directly from tests without `renderHook`, or via `useDoSync` from a
 * React component.
 */
export function runDoSyncEngine({
  provider,
  scopeLabel,
  roomId,
  adapter,
}: {
  provider: EngineProvider;
  scopeLabel: 'user-do' | 'study-group-do';
  roomId: string;
  adapter: ScopeAdapter;
}): () => void {
  const sendRaw: SendRaw = (raw: string) => {
    if (provider.ws?.readyState !== WebSocket.OPEN) return false;
    provider.sendMessage(raw);
    logMessage(scopeLabel, 'out', raw, roomId);
    return true;
  };

  // Track whether the adapter's onConnected has fired this connection
  // cycle. The Yjs `sync` event and the "ws already open at mount"
  // fast path can both arrive; dedupe so adapters don't see two
  // onConnected calls back-to-back.
  let hasFiredOnConnected = false;
  const fireOnConnectedOnce = () => {
    if (hasFiredOnConnected) return;
    hasFiredOnConnected = true;
    perfMark(`startup:socket-ready ${scopeLabel}`, () => ({ room: shortId(roomId) })); // [perf-probe]
    adapter.onConnected?.(sendRaw);
  };

  const onCustomMessage = (payload: string) => {
    try {
      const perfT0 = perfNow(); // [perf-probe]
      const msg = decodeCustomPayload(payload) as unknown;
      const compressed = payload.startsWith('gz:');
      // Only the big frames are worth a line: the base64 + inflate + JSON.parse of
      // a cold-start diff runs synchronously in this callback on the JS thread.
      perfLog(
        `sync-diff:decode ${scopeLabel}`,
        perfT0,
        () => ({
          bytes: payload.length,
          compressed,
          type: (msg as { type?: string })?.type,
        }),
        5,
      ); // [perf-probe]
      logIncoming(scopeLabel, payload, msg, compressed, roomId);

      // ── Universal handlers ──────────────────────────────────────
      if (isPersistenceErrorMessage(msg)) {
        showPersistenceErrorToast(msg);
        return;
      }

      const typed = msg as { type?: string };
      if (typed.type === 'op-ack') {
        // Peek-and-advance scope bookkeeping (e.g. SG shared-card clock)
        // before handleOpAck forgets the flush mutation.
        adapter.onPushAck?.((msg as OpAckMessage).mutationId);
        handleOpAck(msg as OpAckMessage, scopeLabel, roomId);
        return;
      }
      if (typed.type === 'op-rejected') {
        const rejected = msg as OpRejectedMessage;
        const background =
          adapter.onPushRejected?.(rejected.mutationId, sendRaw, rejected.reason) ===
          true;
        handleOpRejected(rejected, scopeLabel, roomId, background);
        return;
      }

      // ── Scope-specific protocol + entity broadcasts ─────────────
      void Promise.resolve(adapter.onProtocolMessage(msg, sendRaw))
        .then((handled) => {
          if (!handled && process.env.NODE_ENV === 'development') {
            console.warn(
              `[${scopeLabel} ${shortId(roomId)}] unhandled message type:`,
              typed.type,
            );
          }
        })
        // Without this, an adapter handler that throws (or rejects) is
        // an UNHANDLED rejection — silently swallowed in most runtimes,
        // and any status transition the handler didn't reach never
        // happens. Adapters own their status failover (see the user-do
        // sync-diff branch); this is the backstop that keeps a bug there
        // observable instead of invisible.
        .catch((err) => {
          console.error(
            `[${scopeLabel} ${shortId(roomId)}] handler failed for ${typed.type}:`,
            err,
          );
        });
    } catch (err) {
      console.error(`[${scopeLabel} ${shortId(roomId)}] message parse error:`, err);
    }
  };

  provider.on('custom-message', onCustomMessage);

  // Fire onConnected if the socket is already open (happens on remount
  // with a warm provider — y-partyserver retains the WS).
  if (provider.ws?.readyState === WebSocket.OPEN) {
    fireOnConnectedOnce();
  }

  // Consecutive auth-failure closes since the last successful sync.
  // See AUTH_CLOSE_TERMINAL_THRESHOLD for why one 4401 is not terminal.
  let authCloseCount = 0;
  // Same, for closes of sockets that carried no token — see
  // NO_TOKEN_RETRY_DELAYS_MS. Never terminal; reported once only when they
  // persist (NO_TOKEN_REPORT_AFTER_PAUSES).
  let noTokenCloseCount = 0;
  let noTokenPauses = 0;
  let noTokenResumeTimer: ReturnType<typeof setTimeout> | null = null;

  const onSync = (synced: boolean) => {
    if (synced) {
      authCloseCount = 0;
      noTokenCloseCount = 0;
      noTokenPauses = 0;
      fireOnConnectedOnce();
    }
  };
  provider.on('sync', onSync);

  const onClose = (event?: WsCloseEvent) => {
    hasFiredOnConnected = false;
    adapter.onDisconnected?.();

    const code = event?.code;
    if (code !== 4401 && code !== 4403) return;
    if (closedSocketCarriedToken(provider) === false) {
      noTokenCloseCount += 1;
      if (noTokenCloseCount < AUTH_CLOSE_TERMINAL_THRESHOLD) return;
      noTokenCloseCount = 0;
      const delay =
        NO_TOKEN_RETRY_DELAYS_MS[
          Math.min(noTokenPauses, NO_TOKEN_RETRY_DELAYS_MS.length - 1)
        ];
      noTokenPauses += 1;
      console.warn(
        `[${scopeLabel} ${shortId(roomId)}] no auth token to connect with — retrying in ${delay / 1000}s`,
      );
      if (
        noTokenPauses >= NO_TOKEN_REPORT_AFTER_PAUSES &&
        !reportedNoTokenScopes.has(scopeLabel)
      ) {
        reportedNoTokenScopes.add(scopeLabel);
        Sentry.captureMessage('sync has no auth token: token-less closes persist', {
          level: 'warning',
          tags: { sync_scope: scopeLabel, ws_close_code: String(code) },
          extra: { no_token_pauses: noTokenPauses },
        });
      }
      provider.disconnect?.();
      if (noTokenResumeTimer) clearTimeout(noTokenResumeTimer);
      noTokenResumeTimer = setTimeout(() => {
        noTokenResumeTimer = null;
        // y-partyserver's connect() is async and rethrows a params failure;
        // partysocket keeps retrying on its own, so the rejection is noise.
        void Promise.resolve(provider.connect?.()).catch((err: unknown) => {
          console.warn(
            `[${scopeLabel} ${shortId(roomId)}] reconnect after no-token pause failed`,
            err,
          );
        });
      }, delay);
      return;
    }
    // The token itself was refused: the JWT caches drop it, so the reconnect's
    // params mint a fresh one instead of re-sending this one until it expires.
    notifySocketCredentialRejected(closedSocketToken(provider));
    authCloseCount += 1;
    if (authCloseCount < AUTH_CLOSE_TERMINAL_THRESHOLD) return;
    // Terminal: the server keeps rejecting this credential. Kill the
    // reconnect loop (partysocket retries forever otherwise) and let
    // the adapter surface + report the state.
    provider.disconnect?.();
    adapter.onAuthRejected?.(code, event?.reason);
  };
  provider.on('connection-close', onClose);

  // Wire outbound dirty subscription.
  const unsubDirty = adapter.subscribeDirty(sendRaw);

  return () => {
    provider.off('custom-message', onCustomMessage);
    provider.off('sync', onSync);
    provider.off('connection-close', onClose);
    if (noTokenResumeTimer) clearTimeout(noTokenResumeTimer);
    unsubDirty();
    // Release adapter-held resources (e.g. the user-do sync-init
    // watchdog). `connection-close` fires asynchronously after
    // `ws.close()`, by which point the listener above is already off —
    // so unbind must dispose explicitly or adapter timers leak.
    adapter.onDispose?.();
  };
}

// ---------------------------------------------------------------------------
// useDoSync hook
// ---------------------------------------------------------------------------

/**
 * React wrapper around `runDoSyncEngine`. Wires the engine into a
 * `useEffect` keyed on the provider + roomId so the engine re-binds
 * when either changes.
 */
export function useDoSync({
  provider,
  scopeLabel,
  roomId,
  adapter,
}: DoSyncEngineConfig): void {
  // Ref so the adapter can be swapped without re-binding the engine.
  const adapterRef = useRef(adapter);
  adapterRef.current = adapter;

  useEffect(() => {
    if (!roomId || !provider) return;
    // Wrap the live adapter ref in a stable adapter object — every
    // method delegates through the ref so consumers can swap the
    // adapter without re-binding.
    const stableAdapter: ScopeAdapter = {
      onProtocolMessage: (m, s) => adapterRef.current.onProtocolMessage(m, s),
      subscribeDirty: (s) => adapterRef.current.subscribeDirty(s),
      onConnected: (s) => adapterRef.current.onConnected?.(s),
      onDisconnected: () => adapterRef.current.onDisconnected?.(),
      onDispose: () => adapterRef.current.onDispose?.(),
      onPushAck: (m) => adapterRef.current.onPushAck?.(m),
      onPushRejected: (m, s, r) => adapterRef.current.onPushRejected?.(m, s, r) ?? false,
      onAuthRejected: (c, r) => adapterRef.current.onAuthRejected?.(c, r),
    };
    return runDoSyncEngine({
      provider: provider as unknown as EngineProvider,
      scopeLabel,
      roomId,
      adapter: stableAdapter,
    });
  }, [provider, roomId, scopeLabel]);
}

// ---------------------------------------------------------------------------
// Universal handlers (op-ack / op-rejected)
// ---------------------------------------------------------------------------
//
// Both scopes settle Phase-5 optimistic-write outcomes identically:
//   - op-ack with `kind === 'acked'` → drop the pre-image
//   - op-rejected with kind in {rejected, partial} → consume + revert
// The toast emission is also shared.

type OpAckMessage = {
  type: 'op-ack';
  mutationId: string;
  scope?: 'shared' | 'personal';
};

type OpRejectedMessage = {
  type: 'op-rejected';
  mutationId: string;
  reason: import('../sync-bookkeeping/pending-mutations').RejectReason;
  scope?: 'shared' | 'personal';
};

function handleOpAck(msg: OpAckMessage, scopeLabel: string, roomId: string): void {
  const outcome = getPendingMutationsTracker().ack(msg.mutationId, msg.scope);
  if (outcome.kind === 'acked') {
    getPreImageStash().drop(msg.mutationId);
  }
  // Successful flush push — drop the reject-guard bookkeeping for it.
  forgetFlushMutation(msg.mutationId);
  void scopeLabel;
  void roomId;
}

function handleOpRejected(
  msg: OpRejectedMessage,
  scopeLabel: string,
  roomId: string,
  background = false,
): void {
  const outcome = getPendingMutationsTracker().reject(
    msg.mutationId,
    msg.reason,
    msg.scope,
  );
  if (outcome.kind === 'rejected' || outcome.kind === 'partial') {
    getPreImageStash().consume(msg.mutationId)?.revert();
  }
  // Flush reconcile push the server refused — remember the version so
  // flushSgLocalDirty stops re-emitting it on every reconnect (no-op for
  // non-flush mutations). Pairs with the toast below for user feedback.
  noteFlushMutationRejected(msg.mutationId);
  if (!background) getRejectionToastEmitter().emit({ reason: msg.reason });
  if (process.env.NODE_ENV === 'development') {
    console.warn(
      `[${scopeLabel} ${shortId(roomId)}] op-rejected mutation=${msg.mutationId} scope=${msg.scope} reason=${msg.reason}`,
    );
  }
}

// ---------------------------------------------------------------------------
// Devtools message log helpers
// ---------------------------------------------------------------------------

function logMessage(
  scopeLabel: 'user-do' | 'study-group-do',
  direction: 'out' | 'in',
  raw: string,
  roomId: string,
): void {
  // Gate BEFORE the parse — this is the second full JSON.parse of every frame
  // in both directions (the engine already decoded inbound frames), and on a
  // platform without a devtools reader it is pure cost.
  if (!isSyncMessageLogEnabled()) return;
  try {
    const parsed = JSON.parse(raw);
    pushSyncMessage({
      source: scopeLabel,
      direction,
      type: parsed.type ?? 'unknown',
      ts: Date.now(),
      sizeBytes: raw.length,
      detail: roomLabel(scopeLabel, roomId),
      payload: parsed,
    });
  } catch {
    // ignore parse errors
  }
}

function logIncoming(
  scopeLabel: 'user-do' | 'study-group-do',
  payload: string,
  parsed: unknown,
  compressed: boolean,
  roomId: string,
): void {
  if (!isSyncMessageLogEnabled()) return;
  if (compressed) {
    pushSyncMessage({
      source: scopeLabel,
      direction: 'in',
      type: (parsed as { type?: string }).type ?? 'unknown',
      ts: Date.now(),
      sizeBytes: payload.length,
      detail: `${roomLabel(scopeLabel, roomId)} compressed`,
      payload: {
        type: (parsed as { type?: string }).type,
        _compressed: true,
        _bytes: payload.length,
        uncompressed: parsed as Record<string, unknown>,
      },
    });
    return;
  }
  logMessage(scopeLabel, 'in', payload, roomId);
}

function roomLabel(scopeLabel: 'user-do' | 'study-group-do', roomId: string): string {
  return scopeLabel === 'study-group-do' ? `sg:${roomId}` : roomId;
}

function shortId(id: string): string {
  return id.length > 8 ? id.slice(0, 8) : id;
}
